import React from 'react';
import {Marketer} from '../../../features/marketers/models/marketer.model';
import {MapPin, Phone, User, Mail, Building, Clock, Activity, Calendar, CheckCircle, XCircle} from 'lucide-react';

interface MarketerDetailsProps {
  marketer: Marketer | undefined;
  onClose?: () => void;
}

const MarketerDetails: React.FC<MarketerDetailsProps> = ({marketer, onClose}) => {
  // Marketer details received

  if (!marketer) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-medium mb-2">اختر مسوقاً من الخريطة</p>
          <p className="text-sm">انقر على أي مسوق في الخريطة لعرض تفاصيله</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: 'active' | 'inactive') => {
    switch (status) {
    case 'active': return 'bg-green-100 text-green-800 border-green-200';
    case 'inactive': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: 'active' | 'inactive') => {
    switch (status) {
    case 'active': return <CheckCircle className="w-4 h-4" />;
    case 'inactive': return <XCircle className="w-4 h-4" />;
    default: return <Activity className="w-4 h-4" />;
    }
  };

  // Use shared date formatting utility for consistency
  const formatDate = (date: Date | undefined) => {
    if (!date) {
      return 'غير محدد';
    }

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      calendar: 'gregory',
      hour12: true
    }).format(date);
  };

  const formatRelativeTime = (date: Date | undefined) => {
    if (!date) {
      return 'غير محدد';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) {
      return `منذ ${diffMinutes} دقيقة`;
    } else if (diffHours < 24) {
      return `منذ ${diffHours} ساعة`;
    } else {
      return `منذ ${diffDays} يوم`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center border-2 border-gray-100">
              <User className="w-8 h-8 text-brand-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {marketer.displayName}
              </h2>
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <Building className="w-4 h-4" />
                مطور تسويقي
              </p>
              <p className="text-xs text-gray-500 mt-1">
                @{marketer.username}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="إغلاق"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Status Card */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
            الحالة
          </h3>

          <div className={`border rounded-lg p-3 ${getStatusColor(marketer.status)}`}>
            <div className="flex items-center gap-2">
              {getStatusIcon(marketer.status)}
              <div>
                <div className="text-xs font-medium opacity-80">حالة المسوق</div>
                <div className="font-bold">{marketer.statusText}</div>
              </div>
            </div>
          </div>

          {marketer.onlineStatus !== undefined && (
            <div className={`border rounded-lg p-3 ${
              marketer.onlineStatus
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-gray-100 text-gray-800 border-gray-200'
            }`}>
              <div className="flex items-center gap-2">
                {marketer.onlineStatus ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                <div>
                  <div className="text-xs font-medium opacity-80">حالة الاتصال</div>
                  <div className="font-bold">{marketer.onlineStatus ? 'متصل' : 'غير متصل'}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
            معلومات الاتصال
          </h3>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">البريد الإلكتروني</div>
                <div className="font-medium text-gray-900">{marketer.email}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">رقم الهاتف</div>
                <div className="font-medium text-gray-900" dir="ltr">{marketer.phoneNumber}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
            معلومات الموقع
          </h3>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-5 h-5 text-gray-500 text-center">📍</div>
              <div>
                <div className="text-xs text-gray-500">الإحداثيات</div>
                <div className="font-medium text-gray-900 text-sm" dir="ltr">
                  {marketer.latitude.toFixed(4)}, {marketer.longitude.toFixed(4)}
                </div>
              </div>
            </div>

            {marketer.distance !== undefined && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="text-xs text-gray-500">المسافة</div>
                  <div className="font-medium text-gray-900">
                    {marketer.distance === 0 ? 'في المنطقة' : `${marketer.distance.toFixed(2)} كم`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activity Information */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
            نشاط المسوق
          </h3>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="w-5 h-5 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">آخر تحديث للموقع</div>
                <div className="font-medium text-gray-900">
                  {formatRelativeTime(marketer.lastLocationUpdate)}
                </div>
                <div className="text-xs text-gray-400">
                  {formatDate(marketer.lastLocationUpdate)}
                </div>
              </div>
            </div>

            {marketer.createdAt && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="text-xs text-gray-500">تاريخ الانضمام</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(marketer.createdAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default MarketerDetails;
